import React from 'react';

import { cn } from '@/lib/utils';
import defaultUserAvatar from '@/assets/images/default-user.png';
import { formatWorkActivityCost, formatWorkActivityDurationLabel } from '@/utils/workActivityForm';
import { getCurrencySymbol } from '@/utils/budgetCalculations';

export interface CreateWorkActivityProfileCardProps {
  activeStep: number;
  className?: string;
  userName?: string;
  profilePicUrl?: string | null;
  hourlyRate?: number;
  currency?: string;
  activityName?: string;
  durationHours?: number;
  durationMinutes?: number;
}

const CreateWorkActivityProfileCard: React.FC<CreateWorkActivityProfileCardProps> = ({
  activeStep,
  className,
  userName,
  profilePicUrl,
  hourlyRate = 0,
  currency = 'EUR',
  activityName,
  durationHours = 0,
  durationMinutes = 0
}) => {
  const currencySymbol = getCurrencySymbol(currency);
  const showActivitySummary = activeStep >= 2;

  return (
    <div className={cn('card flex flex-col items-start md:items-center gap-4 py-5 px-6', className)}>
      {showActivitySummary ? (
        <>
          <h3 className="w-full text-sm text-secondary text-center">Work Activity</h3>
          <div className="space-y-2 flex flex-col items-center text-center">
            <p className="text-base font-semibold leading-tight">{activityName || '—'}</p>
            <p className="text-sm text-secondary leading-normal">
              {formatWorkActivityDurationLabel(durationHours, durationMinutes)}
              {' • '}
              {formatWorkActivityCost(durationHours, durationMinutes, hourlyRate, currency)}
            </p>
          </div>
        </>
      ) : (
        <>
          <h3 className="w-full text-sm text-secondary md:text-center">Your Profile</h3>

          <div className="w-full md:w-auto gap-2 md:gap-4 flex items-row md:flex-col items-center">
            <span className="size-[40px] md:size-[64px]  rounded-full border bg-lightPurple flex items-center justify-center overflow-hidden shrink-0">
              <img
                src={profilePicUrl || defaultUserAvatar}
                alt={userName || 'Profile'}
                className="w-full h-full object-cover"
              />
            </span>

            <div className="space-y-1 flex-1 flex flex-row md:flex-col items-center justify-between md:justify-start text-center">
              <p className="text-base font-semibold leading-tight">{userName || '—'}</p>
              <p className="text-sm leading-normal">
                <span className="font-semibold text-foreground">
                  {currencySymbol}
                  {hourlyRate}
                </span>
                <span className="text-secondary">/hour</span>
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CreateWorkActivityProfileCard;
